import React, { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Swords, Crosshair, Lock } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import CashStrike from "../components/games/CashStrike";

// CA$H STRIKE is THE playable arcade game (beach cannon shooter, finance quiz).
// Debt Slayer is teased as the next drop.
const GAMES = [
    {
        id: "cashstrike",
        icon: Crosshair,
        tint: "#F6D24B",
        available: true,
        titleKey: "game_cashstrike_title",
        descKey: "game_cashstrike_desc",
    },
    {
        id: "debtslayer",
        icon: Swords,
        tint: "#FF3EA5",
        available: false,
        titleKey: "game_debtslayer_title",
        descKey: "game_debtslayer_desc",
    },
];

const MoneyArcade = () => {
    const { language } = useApp();
    const [active, setActive] = useState(null);

    if (active === "cashstrike") {
        // CA$H STRIKE runs as a full-viewport overlay so it fills the whole
        // screen on mobile, iPad, and desktop — no sidebar / no header padding
        // eating into the playfield.
        return (
            <div className="fixed inset-0 z-50 bg-black" data-testid="cashstrike-fullscreen">
                <CashStrike onExit={() => setActive(null)} />
            </div>
        );
    }

    return (
        <AppLayout title={t(language, "nav_arcade")}>
            <div className="mb-6 text-center">
                <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase border-t border-[hsl(var(--border))] pt-1 inline-block">
                    {t(language, "arcade_eyebrow")}
                </div>
                <h1 className="mt-2 font-display font-extrabold text-[hsl(var(--foreground))] text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em]">
                    {t(language, "arcade_title")}
                </h1>
                <p className="mt-3 max-w-2xl mx-auto text-[hsl(var(--muted-foreground))] text-sm md:text-base">
                    {t(language, "arcade_sub")}
                </p>
                <div className="mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] border"
                     style={{ background: "rgba(10,255,176,0.10)", borderColor: "rgba(10,255,176,0.4)", color: "#0AFFB0" }}>
                    <Sparkles className="w-3 h-3" />
                    {language === "es" ? "ARCADE · GRATIS" : "ARCADE · FREE"}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto" data-testid="arcade-grid">
                {GAMES.map((g, i) => {
                    const Icon = g.icon;
                    const playable = g.available;
                    return (
                        <motion.button
                            key={g.id}
                            type="button"
                            onClick={() => playable && setActive(g.id)}
                            disabled={!playable}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.35, delay: i * 0.06 }}
                            className={`text-left rounded-3xl p-6 md:p-7 border transition-all ${
                                playable
                                    ? "bg-[hsl(var(--card))] border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/50 hover:-translate-y-0.5 hover:shadow-xl cursor-pointer"
                                    : "bg-[hsl(var(--card))]/60 border-[hsl(var(--border))]/60 opacity-70 cursor-not-allowed"
                            }`}
                            data-testid={`arcade-card-${g.id}`}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="w-12 h-12 rounded-2xl grid place-items-center"
                                     style={{ background: `${g.tint}18`, color: g.tint, boxShadow: `0 0 24px -6px ${g.tint}55` }}>
                                    <Icon className="w-5 h-5" />
                                </div>
                                {!playable && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em]">
                                        <Lock className="w-3 h-3" />
                                        {t(language, "arcade_coming")}
                                    </span>
                                )}
                            </div>
                            <h3 className="font-display font-bold text-lg md:text-xl text-[hsl(var(--foreground))] tracking-tight">
                                {t(language, g.titleKey)}
                            </h3>
                            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
                                {t(language, g.descKey)}
                            </p>
                            {playable && (
                                <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em]" style={{ color: g.tint }}>
                                    {t(language, "arcade_play")} →
                                </div>
                            )}
                        </motion.button>
                    );
                })}
            </div>
        </AppLayout>
    );
};

export default MoneyArcade;
